import React, { memo, type ReactElement, useMemo } from 'react';
import {
  Box,
  Link as DSLink,
  Loader,
  type MenuOption,
} from '@saviynt/design-system';

import ChipFilter from '../../shared/components/SodControls/ChipFilter';
import { type SodAdvancedFiltersSharedFunc } from '../../shared/components/SodControls/SodControls';
import { ADVANCED_FILTERS_METHODS } from '../../shared/constants';
import {
  useInvertedLocalize,
  useLocalize,
  useTypedSelector,
} from '../../shared/hooks';
import {
  type ActiveAdvancedFilterState,
  type AdvancedFilterState,
} from '../../shared/types';

import {
  useGetAuditColumnsQuery,
  useGetAuditFiltersOptionsQuery,
  useGetEntitlementsMutation,
  useGetFunctionsQuery,
  useLazyGetFunctionsQuery,
} from './data-layer/auditLogApi';
import { useApiCancellation } from './hooks/useApiCancellation';
import AuditTimestampFilter from './AuditTimestampFilter';

import '../../shared/components/SodControls/SodAdvancedFilters.css';

interface AuditAdvancedFiltersProps extends SodAdvancedFiltersSharedFunc {
  activeFilters: ActiveAdvancedFilterState[];
  searchDebounce: number;
  /**
   * Optional override for determining request context (All Logs vs Transaction Overview).
   * Useful in auditor view where the UI tab is local state but Redux may lag on first render.
   */
  activeContextOverride?: 'all-logs' | 'transaction-overview';
  className?: string;
  isGraphMode?: boolean;
  isDisabled?: boolean;
  isClearBtn?: boolean;
  endpoint?: string;
}

function AuditAdvancedFilters({
  className,
  activeFilters,
  searchDebounce,
  activeContextOverride = undefined,
  handleOptionsClick,
  handleFilterSearch,
  handleClearBtn,
  isDisabled,
  isGraphMode,
  isClearBtn,
  endpoint,
}: AuditAdvancedFiltersProps): ReactElement {
  const l = useInvertedLocalize();
  const localize = useLocalize();

  // Get request metadata and API status
  const requestMetadata = useTypedSelector(
    (s) => s.aag.auditLog.requestMetadata
  );
  const isLoadingRequestReviewDetails = useTypedSelector(
    (s) => s.aag.auditLog.isLoadingRequestReviewDetails
  );
  const requestReviewDetailsError = useTypedSelector(
    (s) => s.aag.auditLog.requestReviewDetailsError
  );

  const {
    data: apiColumns,
    error,
    isLoading,
  } = useGetAuditColumnsQuery(
    {
      view: 'securityAuditLog',
      requestAccessKey: requestMetadata?.requestAccessKey ?? '',
    },
    {
      skip: !requestMetadata?.requestAccessKey,
    }
  );
  const [getEntitlements] = useGetEntitlementsMutation();

  const shouldFetchFilterOptions = useMemo(
    () => Boolean(apiColumns) && !error,
    [apiColumns, error]
  );

  // Determine requestType by active context: All Logs vs Transaction Overview
  const reduxActiveContext = useTypedSelector(
    (s) => s.aag.auditLog.activeTab
  ) as string;

  const activeContext = activeContextOverride ?? reduxActiveContext;

  const requestType: 'activitylog' | 'entitlement' =
    activeContext === 'transaction-overview' ? 'entitlement' : 'activitylog';

  const { data: apiOptions } = useGetAuditFiltersOptionsQuery(
    {
      requestAccessKey: requestMetadata?.requestAccessKey ?? '',
      requestType,
    },
    {
      skip:
        !shouldFetchFilterOptions ||
        !requestMetadata ||
        !requestMetadata.requestAccessKey ||
        requestMetadata.requestAccessKey === '' ||
        isLoadingRequestReviewDetails ||
        Boolean(requestReviewDetailsError),
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }
  );

  // Preload Functions list using endpointKey from request metadata
  const endpointKeyRaw = requestMetadata?.endpointKey as unknown;
  const endpointKeyNum =
    typeof endpointKeyRaw === 'number'
      ? endpointKeyRaw
      : Number(endpointKeyRaw);
  const skipFunctions =
    !Number.isFinite(endpointKeyNum) ||
    endpointKeyNum <= 0 ||
    !requestMetadata ||
    typeof requestMetadata.requestAccessKey !== 'number' ||
    requestMetadata.requestAccessKey < 0;
  const { data: functionsData = [] } = useGetFunctionsQuery(
    {
      endpointKey: endpointKeyNum,
      connectionType: requestMetadata?.connectiontype ?? undefined,
      requestAccessKey: requestMetadata?.requestAccessKey ?? '',
    },
    { skip: skipFunctions }
  );
  const [triggerFunctionsSearch] = useLazyGetFunctionsQuery();
  const { registerAbort, cancelRequest } = useApiCancellation();

  const functionNameOptions: MenuOption[] = useMemo(() => {
    const seen = new Set<string>();
    const mapped = functionsData
      .map((rec) => ({ label: rec.functionName, value: rec.functionName }))
      .filter((opt) => {
        const key = String(opt.label);

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);

        return true;
      })
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    return mapped.slice(0, 1000);
  }, [functionsData]);

  const handleFunctionNamesSearch = React.useCallback(
    async (_key: string, searchString: string) => {
      const q = String(searchString).trim();

      if (!Number.isFinite(endpointKeyNum) || endpointKeyNum <= 0) {
        return [];
      }

      try {
        // Call API with functionName param to server-filter results
        // Cancel previous functions search request if any
        cancelRequest('getFunctions');

        const promise = triggerFunctionsSearch({
          endpointKey: endpointKeyNum,
          functionName: q,
          connectionType: requestMetadata?.connectiontype ?? undefined,
          requestAccessKey: requestMetadata?.requestAccessKey ?? '',
        });

        // Register abort function for this request
        const abortablePromise = registerAbort(
          promise as unknown as ReturnType<typeof triggerFunctionsSearch> & {
            abort?: () => void;
          },
          'getFunctions'
        );

        const resp = await abortablePromise.unwrap();

        return (Array.isArray(resp) ? resp : []).slice(0, 500).map((r) => {
          const fn = (r as { functionName?: unknown }).functionName;
          const name = typeof fn === 'string' ? fn : '';

          return { label: name, value: name };
        });
      } catch {
        // Fallback to client-side filter on preloaded options
        const fallback = functionNameOptions.filter((o) =>
          String(o.label).toLowerCase().includes(q.toLowerCase())
        );

        return fallback;
      }
    },
    [
      endpointKeyNum,
      triggerFunctionsSearch,
      functionNameOptions,
      requestMetadata?.connectiontype,
      requestMetadata?.requestAccessKey,
      cancelRequest,
      registerAbort,
    ]
  );

  const finalFilters: AdvancedFilterState[] = useMemo(() => {
    if (error) {
      return [];
    }

    // Build options map from options API
    const optionsMap: Partial<Record<string, MenuOption[]>> = Object.keys(
      apiOptions ?? {}
    ).reduce<Partial<Record<string, MenuOption[]>>>((acc, key) => {
      const entry = (apiOptions as Record<string, { options: MenuOption[] }>)[
        key
      ];

      acc[key] = Array.isArray(entry.options) ? entry.options : [];

      return acc;
    }, {});

    const apiObj = (apiColumns ?? {}) as {
      filters?: { id: string; localizationKey: string; isDefault: boolean }[];
      entitlementFilters?: {
        id: string;
        localizationKey: string;
        isDefault: boolean;
      }[];
    };
    let sourceFilters: {
      id: string;
      localizationKey: string;
      isDefault: boolean;
    }[] = [];

    // Context-based selection:
    // - 'all-logs' => auditLogFilters (apiObj.filters)
    // - 'transaction-overview' => entitlementFilters (apiObj.entitlementFilters)
    if (activeContext === 'transaction-overview') {
      sourceFilters = Array.isArray(apiObj.entitlementFilters)
        ? apiObj.entitlementFilters
        : [];
    } else {
      sourceFilters = Array.isArray(apiObj.filters) ? apiObj.filters : [];
    }

    if (sourceFilters.length === 0) {
      return [];
    }

    // Decide which filters are SEARCH vs CUSTOM (options-driven)
    const searchIds = new Set<string>([
      'entitlementDisplayNames',
      'functionNames',
    ]);

    const mapped: AdvancedFilterState[] = sourceFilters
      .filter((f) => {
        // In transaction-overview: show all entitlementFilters as-is, and DO NOT show logDuration
        if (activeContext === 'transaction-overview') {
          return f.id !== 'logDuration';
        }

        // In all-logs: allow logDuration if present in schema, even if /filters omits it
        if (f.id === 'logDuration') {
          return true;
        }

        const hasInApi = apiOptions
          ? Object.prototype.hasOwnProperty.call(
              apiOptions as Record<string, unknown>,
              f.id
            )
          : false;

        return hasInApi;
      })
      .map((f) => {
        const method = searchIds.has(f.id) ? 'SEARCH' : 'CUSTOM';
        const label = localize(f.localizationKey) || f.id;
        let options = method === 'CUSTOM' ? optionsMap[f.id] ?? [] : [];

        // Title-case labels for changeIndicators
        if (f.id === 'changeIndicators' && options.length > 0) {
          options = options.map((opt) => ({
            ...opt,
            label: String(opt.label)
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase()),
          }));
        }

        return {
          value: f.id,
          label,
          method,
          isChecked: Boolean(f.isDefault),
          options,
        };
      });

    return mapped;
  }, [apiColumns, apiOptions, error, activeContext, localize]);

  const enabledFilters: AdvancedFilterState[] = useMemo(
    () => finalFilters.filter((option) => option.isChecked),
    [finalFilters]
  );

  // Show loading spinner while waiting for request review details
  if (isLoadingRequestReviewDetails) {
    return (
      <Box
        className={['SodAdvancedFilters', className].filter(Boolean).join(' ')}>
        <Box className='SodAdvancedFilters-loading'>
          <Loader kind='dots' format='inline' color='brand' size='small' />
        </Box>
      </Box>
    );
  }

  // Show error if request review details failed
  if (requestReviewDetailsError) {
    return (
      <Box
        className={['SodAdvancedFilters', className].filter(Boolean).join(' ')}>
        <Box className='SodAdvancedFilters-error'>
          {l('Failed to load request details. Please refresh the page.')}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={['SodAdvancedFilters', className].filter(Boolean).join(' ')}>
      {Boolean(error) && (
        <DSLink
          text={l('Failed to load filters. Retry')}
          kind='button'
          onClick={() => {}}
        />
      )}
      {!error && enabledFilters.length === 0 && !isLoading && (
        <Box className='SodAdvancedFilters-error'>
          {l('No filters available')}
        </Box>
      )}
      {enabledFilters.map((filter) => {
        const activeOptions =
          activeFilters.find((f) => f.value === filter.value)?.options ?? [];

        // Date-time picker for Log Duration
        if (filter.value === 'logDuration') {
          return (
            <AuditTimestampFilter
              key={filter.value}
              filter={filter}
              activeOptions={activeOptions}
              footerLink2Text={l('Clear Selection')}
              handleOptionsClick={handleOptionsClick}
              isGraphMode={isGraphMode}
              isDisabled={isDisabled}
            />
          );
        }

        // Entitlement Display Names pill: fetch options from Entitlements API
        if (filter.value === 'entitlementDisplayNames') {
          return (
            <ChipFilter
              key={filter.value}
              value={filter.value}
              label={filter.label}
              method={ADVANCED_FILTERS_METHODS.SEARCH}
              options={[]}
              activeOptions={activeOptions}
              enabledFilterValue={null}
              setEnabledFilterValue={() => {}}
              noDataText={l('Enter at least 1 character to view options.')}
              handleFilterSearch={async (
                _key: string,
                searchString: string
              ) => {
                if (!endpoint) {
                  return false;
                }

                const entQuery = `ent.entitlement_value like '%${searchString}%'`;

                try {
                  const values: string[] = await getEntitlements({
                    endpoint,
                    entQuery,
                    max: 500,
                    offset: 0,
                  }).unwrap();

                  const mapped: MenuOption[] = values.map((v: string) => ({
                    label: v,
                    value: v,
                  }));

                  return mapped;
                } catch {
                  return undefined; // signal error
                }
              }}
              searchDebounce={searchDebounce}
              loadingText={l('Loading...')}
              footerLink1Text={' '}
              footerLink2Text={l('Clear Selection')}
              filterSearchError={l('Search failed, Please try again')}
              handleOptionsClick={handleOptionsClick}
              onFooterLink1Click={() => {}}
              handleClearBtn={handleClearBtn}
              isDisabled={isDisabled}
            />
          );
        }

        // Functions pill: pre-populated options + client-side search
        if (filter.value === 'functionNames') {
          return (
            <ChipFilter
              key={filter.value}
              value={filter.value}
              label={filter.label}
              method={filter.method}
              options={functionNameOptions}
              activeOptions={activeOptions}
              enabledFilterValue={null}
              setEnabledFilterValue={() => {}}
              handleFilterSearch={handleFunctionNamesSearch}
              searchDebounce={searchDebounce}
              noDataText={l('No results found')}
              loadingText={l('Loading...')}
              footerLink1Text={' '}
              footerLink2Text={l('Clear Selection')}
              filterSearchError={l('Search failed, Please try again')}
              handleOptionsClick={handleOptionsClick}
              onFooterLink1Click={() => {}}
              handleClearBtn={handleClearBtn}
              isDisabled={isDisabled}
            />
          );
        }

        // Default rendering via ChipFilter
        return (
          <ChipFilter
            key={filter.value}
            value={filter.value}
            label={filter.label}
            method={filter.method}
            options={filter.options ?? []}
            activeOptions={activeOptions}
            enabledFilterValue={null}
            setEnabledFilterValue={() => {}}
            handleFilterSearch={handleFilterSearch}
            searchDebounce={searchDebounce}
            noDataText='No results found'
            minOptionsForSearch={6}
            loadingText={l('Loading...')}
            footerLink1Text={' '}
            footerLink2Text={l('Clear Selection')}
            filterSearchError={l('Search failed, Please try again')}
            handleOptionsClick={handleOptionsClick}
            onFooterLink1Click={() => {}}
            handleClearBtn={handleClearBtn}
            isDisabled={isDisabled}
          />
        );
      })}
      {isClearBtn && (
        <Box className='SodAdvancedFilters-clearBtn'>
          <DSLink
            text={l('Clear')}
            isDisabled={isDisabled}
            size='medium'
            kind='button'
            onClick={handleClearBtn}
          />
        </Box>
      )}
    </Box>
  );
}

AuditAdvancedFilters.defaultProps = {
  className: '',
  activeContextOverride: undefined,
  isGraphMode: false,
  isDisabled: false,
  isClearBtn: false,
  endpoint: '',
};

export default memo(AuditAdvancedFilters);
